/* eslint-disable camelcase */
import React from "react"
import { useSelector } from "react-redux"

import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo3,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_3oo3,
} from "const"
import { SilResultSubsystem } from "server/models"
import { LangType, StoreType, UserSettings } from "client/store"

import * as Const from "client/components/pdfmake/template/const"
import FormatPercentage, { FormatPercentageString } from "client/pages/sil/format/percentage"

import {
    ResultTable,
    ResultTableAnnotation,
    BlockContent,
} from "../../styled"


interface IsaTr842022Props {
    subsystemList: readonly SilResultSubsystem[]
    showArchitectureConstraints: boolean
    calculationMethodDangerous: boolean
    calculationMethodSafe: boolean
}

export const IsaTr842022 = ({
    subsystemList,
    showArchitectureConstraints,
    calculationMethodDangerous,
    calculationMethodSafe,
}: IsaTr842022Props) => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    return (
        <BlockContent>
            <ResultTable>
                <thead>
                    <tr>
                        <th>{lang.subsystem}</th>
                        <th>{lang.architecture}</th>
                        {
                            showArchitectureConstraints
                            && <th>{lang.architecturalConstraints}</th>
                        }
                        <th>{lang.ti}</th>
                        <th>{lang.di}</th>
                        <th>{lang.mttr}</th>
                        <th>{lang.beta}</th>
                        <th>
                            {calculationMethodSafe && lang.detectedFailuresLeadToSafeState}
                            {calculationMethodDangerous && lang.detectedFailuresLeadToDangerousState}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {
                        subsystemList.map((subsystem) => {
                            let ti = subsystem.isatr842022ti
                            let di = subsystem.isatr842022di
                            let mttr = subsystem.isatr842022mttr
                            let beta: JSX.Element | string = <FormatPercentage data={subsystem.isatr842022beta} tableView />
                            const safeState = lang[subsystem.isatr842022safeState]

                            // calculation method can be or dangerous or safe or "no dangerous/safe failure", never both
                            if (!calculationMethodDangerous && calculationMethodSafe) {
                                if (
                                    subsystem.architecture === ARCHITECTURE_1oo1
                                    || subsystem.architecture === ARCHITECTURE_1oo2
                                    || subsystem.architecture === ARCHITECTURE_1oo3
                                ) {
                                    ti = lang.n_a
                                    mttr = lang.n_a
                                }
                                di = lang.n_a
                                beta = lang.n_a
                            }

                            if (calculationMethodDangerous && !calculationMethodSafe) {
                                if (
                                    subsystem.architecture === ARCHITECTURE_1oo1
                                    || subsystem.architecture === ARCHITECTURE_2oo2
                                    || subsystem.architecture === ARCHITECTURE_3oo3
                                ) {
                                    beta = lang.n_a
                                }
                            }

                            return (
                                <tr key={subsystem.id}>
                                    <td>{subsystem.name}</td>
                                    <td>{subsystem.architecture}</td>
                                    {
                                        showArchitectureConstraints
                                        && <td>{lang[subsystem.architecturalConstraints]}</td>
                                    }
                                    <td>{ti}</td>
                                    <td>{di}</td>
                                    <td>{mttr}</td>
                                    <td>{beta}</td>
                                    <td>{safeState}</td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </ResultTable>

            <ResultTableAnnotation>{`${lang.n_a} = ${lang.notApplicable}`}</ResultTableAnnotation>
        </BlockContent>
    )
}


export const IsaTr842022PdfExport = (
    subsystemList: readonly SilResultSubsystem[],
    showArchitectureConstraints: boolean,
    calculationMethodDangerous: boolean,
    calculationMethodSafe: boolean,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const stack: any[] = []
    subsystemList.forEach((subsystem) => {
        let ti = subsystem.isatr842022ti
        let di = subsystem.isatr842022di
        let mttr = subsystem.isatr842022mttr
        let beta = FormatPercentageString(subsystem.isatr842022beta, userSettings, lang)
        const safeState = lang[subsystem.isatr842022safeState]

        if (!calculationMethodDangerous && calculationMethodSafe) {
            if (
                subsystem.architecture === ARCHITECTURE_1oo1
                || subsystem.architecture === ARCHITECTURE_1oo2
                || subsystem.architecture === ARCHITECTURE_1oo3
            ) {
                ti = null
                mttr = null
            }
            di = null
            beta = null
        }

        if (calculationMethodDangerous && !calculationMethodSafe) {
            if (
                subsystem.architecture === ARCHITECTURE_1oo1
                || subsystem.architecture === ARCHITECTURE_2oo2
                || subsystem.architecture === ARCHITECTURE_3oo3
            ) {
                beta = null
            }
        }

        const list: string[] = [
            `${lang.architecture}: ${subsystem.architecture}`,
        ]
        if (showArchitectureConstraints) {
            list.push(`${lang.architecturalConstraints}: ${lang[subsystem.architecturalConstraints]}`)
        }

        if (ti !== null) list.push(`${lang.ti}: ${ti} ${lang.hours}`)
        if (di !== null) list.push(`${lang.di}: ${di} ${lang.hours}`)
        if (mttr !== null) list.push(`${lang.mttr}: ${mttr} ${lang.hours}`)
        if (beta !== null) list.push(`${lang.beta}: ${beta}`)
        if (safeState !== null) {
            if (calculationMethodSafe) list.push(`${lang.detectedFailuresLeadToSafeState}: ${safeState}`)
            if (calculationMethodDangerous) list.push(`${lang.detectedFailuresLeadToDangerousState}: ${safeState}`)
        }

        stack.push(
            {
                bold: true,
                text: `${lang.subsystem} - ${subsystem.name}`,
                margin: Const.SUBSYSTEM_NAME_MARGIN,
            },
            {
                style: Const.LIST,
                ul: list,
            },
        )
    })

    return stack
}
